import { Stack } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TConnectionConfigurationInputSchema } from '@/console/features/devices/components/create-device/create-device.types';
import { TCreateFTPConfigurationFormProps } from '@/console/features/devices/components/create-ftp-configuration/create-ftp-configuration-form.types';
import { TCreateFTPConfigurationInputSchema } from '@/console/features/devices/components/create-ftp-configuration/create-ftp-configuration.types';
import { FormSelectSecret } from '@/console/features/vaults/components/form-select-secret/form-select-secret';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { IpAddressFormInput } from '@/shared/components/ui/form/components/form-input/variant/ip-address-form-input/ip-address-form-input';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import { FormContent } from '@/shared/components/ui/form/form-content';
import { OverlayLoader } from '@/shared/components/ui/overlay-loader/overlay-loader';
import { FtpProtocolEnum } from '@/shared/types/types.generated';

export const CreateFtpConfigurationForm: React.FC<
  TCreateFTPConfigurationFormProps
> = ({ isLoading, disabled }) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<TConnectionConfigurationInputSchema>();

  const getProtocolOptions = () => {
    return [
      {
        label: 'SFTP',
        value: FtpProtocolEnum.Sftp,
      },
      {
        label: 'FTP',
        value: FtpProtocolEnum.Ftp,
      },
    ];
  };

  const handleProtocolChange = (protocol: FtpProtocolEnum) => {
    if (protocol === FtpProtocolEnum.Ftp) {
      setValue('ftpConfiguration.port', 21);
      return;
    }

    setValue('ftpConfiguration.port', 22);
  };

  if (isLoading) {
    return <OverlayLoader display />;
  }

  return (
    <FormContent>
      <IpAddressFormInput<TCreateFTPConfigurationInputSchema>
        name="ftpConfiguration.ipAddress"
        label={t('device:ip_address_plc')}
        hideOptionalLabel
        disabled={disabled}
      />
      <Stack direction="row" gap="0.5rem" alignItems="center">
        <FormSelect<TConnectionConfigurationInputSchema>
          name="ftpConfiguration.protocol"
          label={t('device:protocol')}
          options={getProtocolOptions()}
          onChange={(event) =>
            handleProtocolChange(event.target.value as FtpProtocolEnum)
          }
          required
          disabled={disabled}
        />
        <FormInput<TConnectionConfigurationInputSchema>
          name="ftpConfiguration.port"
          label={t('device:port')}
          type="number"
          hideOptionalLabel
          disabled={disabled}
        />
      </Stack>
      <FormInput<TCreateFTPConfigurationInputSchema>
        name="ftpConfiguration.rootDirectory"
        label={t('device:root_directory')}
        hideOptionalLabel
        disabled={disabled}
      />
      <FormSelectSecret<TCreateFTPConfigurationInputSchema>
        basePath="ftpConfiguration"
        label={t('device:add_ftp_credentials')}
        allowCreateSecret
      />
    </FormContent>
  );
};
